import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  MapPin,
  Loader2,
  Compass,
  Navigation,
  Phone,
  Search,
} from "lucide-react";
import { toast } from "sonner";

// Uses OpenStreetMap Overpass API — free, no key needed.
// Falls back to Google Maps deep-link if geolocation is denied.

const OVERPASS = "https://overpass-api.de/api/interpreter";

const buildQuery = (lat, lon, radiusM = 4000) => `
[out:json][timeout:20];
(
  node["amenity"~"hospital|clinic"](around:${radiusM},${lat},${lon});
  way["amenity"~"hospital|clinic"](around:${radiusM},${lat},${lon});
  relation["amenity"~"hospital|clinic"](around:${radiusM},${lat},${lon});
);
out center 40;
`;

const haversineKm = (a, b) => {
  const R = 6371;
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

export default function HospitalFinder() {
  const [coords, setCoords] = useState(null);
  const [loading, setLoading] = useState(false);
  const [places, setPlaces] = useState([]);
  const [manualQuery, setManualQuery] = useState("");
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const locate = () => {
    if (!navigator.geolocation) {
      setDenied(true);
      toast.error("Geolocation not supported in this browser");
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const c = {
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        };
        setCoords(c);
        await fetchNearby(c);
      },
      (err) => {
        setDenied(true);
        setLoading(false);
        toast.error(
          err.code === 1
            ? "Location permission denied"
            : "Could not get your location"
        );
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const fetchNearby = async (c) => {
    try {
      const q = buildQuery(c.lat, c.lon, 5000);
      const res = await fetch(OVERPASS, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: `data=${encodeURIComponent(q)}`,
      });
      const data = await res.json();
      const items = (data.elements || [])
        .map((el) => {
          const lat = el.lat ?? el.center?.lat;
          const lon = el.lon ?? el.center?.lon;
          if (!lat || !lon) return null;
          const tags = el.tags || {};
          return {
            id: el.id,
            name: tags.name || "Unnamed facility",
            kind: tags.amenity || "hospital",
            phone: tags.phone || tags["contact:phone"] || "",
            address: [
              tags["addr:street"],
              tags["addr:city"],
              tags["addr:state"],
            ]
              .filter(Boolean)
              .join(", "),
            lat,
            lon,
            distance_km: haversineKm(c, { lat, lon }),
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.distance_km - b.distance_km)
        .slice(0, 30);
      setPlaces(items);
      if (items.length === 0) {
        toast.warning("No facilities in OpenStreetMap around here yet.");
      }
    } catch (e) {
      toast.error("Could not load nearby facilities. Try Google Maps.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05050A] text-white pb-24">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 opacity-70"
        style={{
          background:
            "radial-gradient(45% 30% at 10% 0%, rgba(52,211,153,0.14) 0%, transparent 60%), radial-gradient(45% 30% at 95% 20%, rgba(0,229,255,0.15) 0%, transparent 60%)",
        }}
      />
      <div className="border-b border-white/5 sticky top-0 z-30 backdrop-blur-md bg-black/40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            data-testid="hosp-back-home"
            className="inline-flex items-center gap-2 text-zinc-300 hover:text-white text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            SilentScan<span className="text-cyan-400">.AI</span>
          </Link>
          <a
            data-testid="hosp-call-108"
            href="tel:108"
            className="text-xs bg-red-500 text-black font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5"
          >
            <Phone className="w-3.5 h-3.5" />
            108
          </a>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 pt-12">
        <div className="max-w-3xl">
          <div className="text-[10px] font-semibold uppercase tracking-[0.35em] text-emerald-300/80">
            Chapter 09 · Nearby Care
          </div>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl font-semibold leading-[1.02]">
            The nearest hospital,{" "}
            <span className="italic text-cyan-300">right now.</span>
          </h1>
          <p className="mt-5 text-zinc-300 max-w-xl text-lg leading-relaxed">
            One tap for your location, and we&apos;ll surface the nearest
            hospitals and clinics from OpenStreetMap.
          </p>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <button
            data-testid="hosp-locate"
            onClick={locate}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-full bg-white text-black font-medium px-5 py-3 text-sm hover:bg-cyan-100 disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Searching
              </>
            ) : (
              <>
                <Compass className="w-4 h-4" />
                Use my location
              </>
            )}
          </button>
          <form
            className="flex-1 min-w-[220px] relative"
            onSubmit={(e) => {
              e.preventDefault();
              const q = encodeURIComponent(
                manualQuery.trim() || "hospitals near me"
              );
              window.open(`https://www.google.com/maps/search/${q}`, "_blank");
            }}
          >
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              data-testid="hosp-search"
              value={manualQuery}
              onChange={(e) => setManualQuery(e.target.value)}
              placeholder="Or search on Google Maps…"
              className="w-full pl-10 pr-4 py-3 rounded-full bg-white/5 border border-white/10 text-white placeholder-zinc-500 focus:outline-none focus:border-cyan-400"
            />
          </form>
        </div>

        {denied && (
          <div className="mt-6 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-100">
            Location permission is denied. You can still search Google Maps
            using the box above.
          </div>
        )}

        {coords && (
          <div className="mt-4 text-[11px] text-zinc-500">
            Anchored to your location · accuracy ~{Math.round(coords.accuracy || 0)} m
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {places.map((p) => (
            <motion.div
              key={p.id}
              data-testid={`hosp-item-${p.id}`}
              whileHover={{ y: -3 }}
              className="rounded-3xl border border-white/10 bg-white/[0.03] p-5"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
                    {p.kind}
                  </div>
                  <div className="font-display text-lg font-semibold text-white leading-tight mt-1 truncate">
                    {p.name}
                  </div>
                  {p.address && (
                    <div className="text-xs text-zinc-400 mt-1 line-clamp-2">
                      {p.address}
                    </div>
                  )}
                </div>
                <div className="flex-shrink-0 text-right">
                  <div className="font-display text-xl font-semibold text-cyan-300 tabular-nums">
                    {p.distance_km.toFixed(1)}
                  </div>
                  <div className="text-[10px] text-zinc-500">km</div>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lon}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-cyan-500 text-black text-xs font-medium px-3 py-2 hover:bg-cyan-400"
                >
                  <Navigation className="w-3.5 h-3.5" />
                  Directions
                </a>
                {p.phone && (
                  <a
                    href={`tel:${p.phone}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/10 text-xs text-white px-3 py-2 hover:bg-white/5"
                  >
                    <Phone className="w-3.5 h-3.5" />
                    Call
                  </a>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {!loading && places.length === 0 && !denied && (
          <div className="mt-10 rounded-3xl border border-white/10 bg-white/[0.03] p-8 text-center">
            <MapPin className="w-6 h-6 text-cyan-300 mx-auto" />
            <div className="mt-3 font-display text-lg font-semibold">
              Tap &quot;Use my location&quot; to find nearby facilities.
            </div>
            <div className="mt-1 text-sm text-zinc-400">
              We never store your location. It stays on your device.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
